import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import dkLogo from '@/assets/damian-keyes-logo.png';

const navItems = [
  { label: 'Free 20 Day Release Plan', href: 'https://www.dk-mba.com/singlereleaseplan', external: true },
  { label: 'My Academy', href: 'https://www.dk-mba.com', external: true },
  { label: 'My Agency', href: 'https://www.dk-artists.com', external: true },
];

export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [lightMode, setLightMode] = useState(false);
  const navRef = useRef<HTMLElement | null>(null);

  // Scroll detection for size change
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrolled = window.scrollY > 20;
          if (scrolled !== isScrolled) {
            setIsScrolled(scrolled);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isScrolled]);

  // Theme detection based on section below nav
  useEffect(() => {
    let ticking = false;

    const detectThemeUnderNav = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const navBottom = navRef.current?.getBoundingClientRect().bottom ?? 72;
        const x = window.innerWidth / 2;
        const y = Math.min(navBottom + 1, window.innerHeight - 1);

        let el = document.elementFromPoint(x, y) as HTMLElement | null;
        let theme: 'light' | 'dark' | null = null;

        // Climb up to find an explicit theme marker
        let cur: HTMLElement | null = el;
        while (cur && cur !== document.body) {
          const attr = cur.getAttribute('data-nav-theme');
          if (attr === 'light' || attr === 'dark') {
            theme = attr as 'light' | 'dark';
            break;
          }
          cur = cur.parentElement;
        }

        if (theme) {
          setLightMode(theme === 'light');
        }

        ticking = false;
      });
    };

    window.addEventListener('scroll', detectThemeUnderNav, { passive: true });
    window.addEventListener('resize', detectThemeUnderNav, { passive: true });
    detectThemeUnderNav();

    return () => {
      window.removeEventListener('scroll', detectThemeUnderNav);
      window.removeEventListener('resize', detectThemeUnderNav);
    };
  }, []);

  return (
    <div className="fixed top-0 left-0 right-0 z-50 pt-3 md:pt-6">
      <div className={`
        ${isScrolled ? 'max-w-7xl lg:max-w-5xl' : 'max-w-7xl'}
        mx-auto px-6 lg:px-12
        transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]
      `}>
        <nav 
          ref={navRef}
          className={`
            rounded-2xl
            py-2 px-4 md:py-3 md:px-6
            ${lightMode 
              ? 'bg-neutral-50 border border-neutral-100 shadow-sm' 
              : 'bg-black border border-white/10 shadow-sm'
            }
          `}
        >
        <div className="flex items-center justify-between">
          <Link to="/" className="cursor-pointer hover:opacity-90 transition-opacity duration-200">
            <img 
              src={dkLogo} 
              alt="DK Music Group" 
              className={`
                h-8 md:h-10 w-auto object-contain
                transition-all duration-300
                ${lightMode ? '' : 'brightness-0 invert'}
              `}
            />
          </Link>
          
          {/* Mobile Apply Button */}
          <div className="md:hidden">
            <Button 
              asChild
              className="bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-xl font-semibold h-8 px-4 text-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
            >
              <a href="https://a23j5fry30t.typeform.com/to/CzjaFT5z" target="_blank" rel="noopener noreferrer">
                Apply
              </a>
            </Button>
          </div>
          
          <div className="hidden md:flex items-center space-x-6">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                target={item.external ? "_blank" : undefined}
                rel={item.external ? "noopener noreferrer" : undefined}
                className={`
                  relative font-geist font-medium tracking-tight text-sm
                  transition-colors duration-200
                  after:content-[''] after:absolute after:left-0 after:bottom-0 
                  after:h-[2px] after:w-full after:origin-left
                  after:scale-x-0 after:transition-transform after:duration-300
                  hover:after:scale-x-100
                  ${lightMode
                    ? 'text-neutral-900 hover:text-neutral-600 after:bg-neutral-900'
                    : 'text-white/90 hover:text-white after:bg-white'
                  }
                `}
              >
                {item.label}
              </a>
            ))}
            
            <Button 
              asChild
              className="bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-xl font-semibold h-8 px-4 md:h-10 md:px-5 text-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
            >
              <a href="https://a23j5fry30t.typeform.com/to/CzjaFT5z" target="_blank" rel="noopener noreferrer">
                Apply
              </a>
            </Button>
          </div>
        </div>
      </nav>
      </div>
    </div>
  );
}